import { zodResolver } from '@hookform/resolvers/zod';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Copy, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useConfig } from '@/shared/config';
import { notifyApiError, useNotifier } from '@/shared/data';
import { DocumentFormField, MaskedFormField, SelectFormField, useConfirm } from '@/shared/form';
import { isValidCnpj } from '@/shared/format/cnpj';
import { isValidCpf } from '@/shared/format/cpf';
import { formatDocument, stripDocumentSeparators } from '@/shared/format/document';
import { ApiError } from '@/shared/http';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';

import {
  authorizationLinks,
  authorizationRequestBody,
  checkDataprevAuthorization,
  dataprevKey,
  dataprevRestrictions,
  dataprevWrite,
  getDataprevMargin,
  isMissingPermission,
  listDataprevAuthorizations,
  marginDocument,
  requestDataprevAuthorization,
  requestDataprevMargin,
  type AuthorizationChannel,
  type DataprevMargin,
  type MarginWrite,
} from '../credit-note.margin';
import type { CreditNote, CreditProduct } from '../credit-note.wire';
import { copyAuthorizationLink, MarginAuthorizationBody, type MarginAuthorization } from './margin-authorization';
import { useDataprevMarginOptions } from './margin-options';
import { MarginCards, MarginPanel } from './margin-panel';
import { applyMarginWrite, formProductId } from './margin-write';
import { useTabsForm } from './tab-form';

// A consulta da margem do Crédito do Trabalhador (MarginQueryFormContainer.tsx com o produto Dataprev,
// DataPrevAuthorizationContainer.tsx, SelectWarranty*.tsx, DataprevErrorDrawer.tsx), uma fonte só para a Operação
// ("Consultar Crédito Trabalhador", creditNoteActions.tsx:1089-1100) e a Simulação (porta, R5): CPF → autorização do
// titular em cache → margens guardadas → escolha → `onApply`. Sem autorização, o titular é chamado por WhatsApp, SMS ou
// link web, com a consulta de 5 em 5 s (SMS e link) ou de 30 em 30 s (WhatsApp). A consulta nova (paga) só sai pelo
// botão do legado e depois da prévia (U53, U24). Fora do agente. Fases no `MarginPanel` (Fluxo guiado, família E).

type Phase = 'form' | 'authorization' | 'select' | 'restrictions';

interface FormValues {
  registrationNumber: string;
  authorizationChannel: AuthorizationChannel;
  phoneNumber: string | null;
}

/** `cpfCnpjValidation` (formFieldValidator.ts:47-70): 11 é CPF, 14 é CNPJ, outro tamanho não vale. */
export function validDocument(raw: string): boolean {
  if (raw.length === 11) return isValidCpf(raw);
  if (raw.length === 14) return isValidCnpj(raw);
  return false;
}

/** O `setTimeout(1500)` entre o pedido de autorização e a lista (useAuthorizationLinkRetriever.ts:70). */
const LINK_DELAY_MS = 1500;
/** O polling da autorização (usePostDataprevCacheAuthorization com `enablePolling`, dataprev/index.ts:243). */
const POLL_MS = 5000;
/** O WhatsApp reconsulta a autorização de 30 em 30 s (DataPrevAuthorizationContainer.tsx:117-137). */
const WHATSAPP_RECHECK_MS = 30_000;

export interface DataprevMarginSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** O título da fase de consulta ("Consultar crédito trabalhador", "Consultar margem"). */
  readonly title: string;
  /** O CPF (cru); com ele o campo trava (DataPrevAuthorizationContainer.tsx:384). */
  readonly registrationNumber: string;
  readonly personId: string | null | undefined;
  /** O produto do `GET`/`POST` da margem. */
  readonly creditProductId: string | null | undefined;
  /** O produto do pedido de autorização (`productResponse?.id`, useAuthorizationLinkRetriever.ts:55). */
  readonly authorizationProductId: string | null | undefined;
  /** A Operação aceita CPF ou CNPJ (`cpfCnpjValidation`); a Simulação, só CPF. */
  readonly acceptCnpj?: boolean;
  /** "Confirmar" com a escolha (SelectWarrantyContainer.tsx:142-192): quem abriu escreve. */
  readonly onApply: (write: MarginWrite) => void;
}

const STEP_QUERY = { id: 'form', title: msg`Consulta` };
const STEP_AUTHORIZATION = { id: 'authorization', title: msg`Autorização` };
const STEP_SELECT = { id: 'select', title: msg`Margens` };
const STEP_RESTRICTIONS = { id: 'restrictions', title: msg`Restrições` };

export function DataprevMarginSheet({ open, onOpenChange, title, registrationNumber, personId, creditProductId, authorizationProductId, acceptCnpj = false, onApply }: DataprevMarginSheetProps) {
  const { t } = useLingui();
  const config = useConfig() as Record<string, unknown>;
  const notifier = useNotifier();
  const [confirm, confirmElement] = useConfirm();
  const formId = useId();
  const [phase, setPhase] = useState<Phase>('form');
  const [document, setDocument] = useState(registrationNumber);
  const [authRequired, setAuthRequired] = useState(false);
  const [cache, setCache] = useState<Record<string, unknown> | undefined>(undefined);
  const [authorization, setAuthorization] = useState<MarginAuthorization | null>(null);
  const [smsPolling, setSmsPolling] = useState(false);
  const [margin, setMargin] = useState<DataprevMargin | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const [restrictions, setRestrictions] = useState<ReturnType<typeof dataprevRestrictions> | null>(null);
  const [busy, setBusy] = useState(false);
  const [querying, setQuerying] = useState(false);

  const valid = (value: string) => (acceptCnpj ? validDocument(stripDocumentSeparators(value)) : isValidCpf(stripDocumentSeparators(value)));
  const schema = z.object({
    registrationNumber: z
      .string()
      .refine((value) => value.trim() !== '', t`CPF: precisa ser preenchido.`)
      .refine((value) => value.trim() === '' || valid(value), t`CPF é inválido.`),
    authorizationChannel: z.enum(['WhatsappInbound', 'SMS', 'WebLink']),
    phoneNumber: z.string().nullable(),
  });
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    // MarginQueryFormContainer.tsx:623-628: fora do INSS, o canal padrão é o WhatsApp.
    defaultValues: { registrationNumber, authorizationChannel: 'WhatsappInbound', phoneNumber: null },
    mode: 'onTouched',
  });
  const [channel, typed] = useWatch({ control: form.control, name: ['authorizationChannel', 'registrationNumber'] });
  // O CPF válido digitado vira o da consulta e o campo trava (`onChangeField` + `disabled={!!registrationNumber}`).
  const locked = registrationNumber !== '' || valid(typed);

  const params = (cpf: string) => ({ registrationNumber: cpf, personId, creditProductId });
  const close = () => onOpenChange(false);

  /** `dataprevRefetch` (MarginQueryFormContainer.tsx:246-257): com lista (mesmo vazia) abre a escolha; erro, as restrições. */
  const loadMargins = async (cpf: string, options: { readonly requireData?: boolean; readonly success?: string } = {}) => {
    try {
      const list = await getDataprevMargin(params(cpf));
      if (options.requireData && list.length === 0) return;
      setMargin(list[0]);
      setSelected([]);
      setPhase('select');
      if (options.success) notifier.notify({ tone: 'success', title: t`Autorização concedida!`, description: options.success });
    } catch (error) {
      setRestrictions(dataprevRestrictions(error));
      setPhase('restrictions');
    }
  };

  const requestAuthorization = async (values: FormValues) => {
    setBusy(true);
    try {
      const response = await requestDataprevAuthorization(
        authorizationRequestBody({ registrationNumber: values.registrationNumber, phoneNumber: values.phoneNumber, productId: authorizationProductId, channel: values.authorizationChannel }),
      );
      await new Promise((resolve) => window.setTimeout(resolve, LINK_DELAY_MS));
      const links = authorizationLinks(response, await listDataprevAuthorizations(values.registrationNumber));
      setAuthorization({ channel: values.authorizationChannel, ...links, phone: values.phoneNumber });
      setSmsPolling(values.authorizationChannel === 'SMS');
      setPhase('authorization');
    } catch (error) {
      notifyApiError(notifier, error);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (values: FormValues) => {
    const cpf = stripDocumentSeparators(values.registrationNumber);
    setDocument(cpf);
    if (authRequired) {
      await requestAuthorization(values);
      return;
    }
    setBusy(true);
    try {
      if (!config['SKIP_DATAPREV_AUTHORIZATION']) {
        try {
          setCache(await checkDataprevAuthorization(cpf));
        } catch (error) {
          // 403 sem permissão: o aviso "Acesso negado" e nada mais (dataprev/index.ts:271-288).
          if (isMissingPermission(error)) {
            const message = (ApiError.from(error).payload as { message?: unknown }).message;
            notifier.notify({ tone: 'error', title: t`Acesso negado`, ...(typeof message === 'string' ? { description: message } : {}) });
            return;
          }
          setAuthRequired(true);
          return;
        }
        setAuthRequired(false);
      }
      await loadMargins(cpf);
    } finally {
      setBusy(false);
    }
  };

  // SMS: de 5 em 5 s depois do envio; link web: enquanto o canal escolhido for o link (MarginQueryFormContainer.tsx:367-433).
  const webPolling = open && authRequired && channel === 'WebLink' && phase === 'form';
  const pollKind = smsPolling && open ? 'sms' : webPolling ? 'weblink' : null;
  const onAuthorizedRef = useRef<(kind: 'sms' | 'weblink', data: Record<string, unknown>) => void>(() => undefined);
  useEffect(() => {
    onAuthorizedRef.current = (kind, data) => {
      setSmsPolling(false);
      setAuthRequired(false);
      setCache(data);
      // useAuthorizationPolling.ts:38-40: o SMS fecha o painel da autorização; a escolha só abre com margem guardada.
      if (kind === 'sms') {
        setAuthorization(null);
        setPhase('form');
      }
      void loadMargins(document, {
        requireData: true,
        success: kind === 'sms' ? t`O titular autorizou a consulta. Agora você pode visualizar as margens disponíveis.` : t`O titular autorizou a consulta via link web. Agora você pode visualizar as margens disponíveis.`,
      });
    };
  });
  useEffect(() => {
    if (pollKind === null || document === '') return undefined;
    const controller = new AbortController();
    let done = false;
    const tick = async () => {
      try {
        const data = await checkDataprevAuthorization(document, controller.signal);
        if (done) return;
        done = true;
        window.clearInterval(timer);
        onAuthorizedRef.current(pollKind, data);
      } catch {
        // Ainda sem autorização: a próxima volta pergunta de novo.
      }
    };
    void tick();
    const timer = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      done = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [pollKind, document]);

  const cacheEmpty = Object.keys(cache ?? {}).length === 0;
  const recheck = async () => {
    try {
      setCache(await checkDataprevAuthorization(document));
      setAuthRequired(false);
    } catch {
      // Ainda sem autorização: o formulário continua pedindo o canal.
    }
  };
  const recheckRef = useRef(recheck);
  useEffect(() => {
    recheckRef.current = recheck;
  });
  // WhatsApp: a autorização é reconsultada de 30 em 30 s enquanto o painel está aberto e vazio.
  const whatsappWaiting = phase === 'authorization' && authorization?.channel === 'WhatsappInbound' && cacheEmpty;
  useEffect(() => {
    if (!whatsappWaiting) return undefined;
    const timer = window.setInterval(() => void recheckRef.current(), WHATSAPP_RECHECK_MS);
    return () => window.clearInterval(timer);
  }, [whatsappWaiting]);

  /** `handleCloseDrawerAuthorization` (MarginQueryFormContainer.tsx:203-209): volta, reconsulta e para o polling. */
  const closeAuthorization = () => {
    setPhase('form');
    setAuthorization(null);
    setSmsPolling(false);
    void recheck();
  };

  const options = useDataprevMarginOptions(margin);

  /** "Obter margem"/"Atualizar margem" (MarginQueryFormContainer.tsx:770-777): a consulta nova (paga), com prévia, e a releitura. */
  const requestMargin = async () => {
    const formatted = formatDocument(document) ?? document;
    const ok = await confirm({
      title: t`Nova consulta de margem`,
      description: t`A consulta da margem do crédito do trabalhador do CPF ${formatted} na Dataprev é paga: cada nova consulta é cobrada e substitui a margem guardada.`,
      confirmLabel: t`Sim, consultar`,
      tone: 'default',
    });
    if (!ok) return;
    setQuerying(true);
    try {
      await requestDataprevMargin(params(document));
      // MarginQueryFormContainer.tsx:165, 196-201.
      notifier.notify({ tone: 'success', title: t`Consulta de margem realizada com sucesso!` });
    } catch (error) {
      notifyApiError(notifier, error);
      setQuerying(false);
      return;
    }
    try {
      const list = await getDataprevMargin(params(document));
      setMargin(list[0]);
    } catch (error) {
      setRestrictions(dataprevRestrictions(error));
      setPhase('restrictions');
    } finally {
      setQuerying(false);
    }
  };

  const confirmSelection = () => {
    // SelectWarrantyContainer.tsx:86-88, 151-153.
    if (selected.length === 0) {
      notifier.notify({ tone: 'warning', title: t`Selecione ao menos 1 modelo de margem` });
      return;
    }
    const write = dataprevWrite((margin?.result ?? []).filter((item) => selected.includes(dataprevKey(item))));
    if (!write) {
      notifier.notify({ tone: 'warning', title: t`Nenhum item selecionado encontrado` });
      return;
    }
    // SelectWarrantyContainer.tsx:174-192: quem abriu escreve e o painel fecha, sem prévia (nada vai ao servidor).
    onApply(write);
    close();
  };

  /** `handleCloseErrorDrawer`/`handleBackErrorDrawer` (MarginQueryFormContainer.tsx:330-347): zera e fecha ou volta. */
  const resetStates = () => {
    setRestrictions(null);
    setMargin(undefined);
    setAuthorization(null);
    setPhase('form');
  };

  const onPanelChange = (next: boolean) => {
    if (!next && phase === 'authorization') {
      closeAuthorization();
      return;
    }
    onOpenChange(next);
  };

  const count = restrictions?.items.length ?? 0;
  const titles: Record<Phase, string> = {
    form: title,
    authorization: authorization?.channel === 'SMS' ? t`SMS enviado!` : cacheEmpty ? t`Entre em contato via WhatsApp` : t`Autorização concluída`,
    select: t`Selecione uma das margens disponíveis`,
    restrictions: count === 0 ? t`Atenção!` : count === 1 ? t`Atenção, foi encontrada 1 restrição para o CPF consultado` : t`Atenção, foram encontradas ${count} restrições para o CPF consultado`,
  };
  const steps = [STEP_QUERY, ...(authRequired || phase === 'authorization' ? [STEP_AUTHORIZATION] : []), phase === 'restrictions' ? STEP_RESTRICTIONS : STEP_SELECT];

  const submitLabel = authRequired ? (channel === 'SMS' ? t`Enviar SMS de autorização` : t`Autorizar via WhatsApp`) : t`Consultar`;
  const footers: Record<Phase, ReactNode> = {
    form:
      authRequired && channel === 'WebLink' ? undefined : (
        <Button type="submit" form={formId} loading={busy}>
          {submitLabel}
        </Button>
      ),
    authorization:
      authorization?.channel === 'WhatsappInbound' && authorization.link && cacheEmpty ? (
        <Button type="button" onClick={() => copyAuthorizationLink(notifier, authorization.link, t)}>
          <Copy aria-hidden />
          {t`Copiar link`}
        </Button>
      ) : undefined,
    select: (
      <>
        <Button type="button" variant="secondary" loading={querying} onClick={() => void requestMargin()}>
          <RefreshCw aria-hidden />
          {margin ? t`Atualizar margem` : t`Obter margem`}
        </Button>
        <Button type="button" onClick={confirmSelection}>
          {t`Confirmar`}
        </Button>
      </>
    ),
    restrictions: (
      <Button
        type="button"
        onClick={() => {
          resetStates();
          close();
        }}
      >
        {t`Fechar`}
      </Button>
    ),
  };
  const backs: Record<Phase, (() => void) | undefined> = {
    form: undefined,
    authorization: closeAuthorization,
    select: () => setPhase('form'),
    restrictions: resetStates,
  };

  const webLink = typeof config['DATAPREV_AUTHORIZATION_WEB_LINK'] === 'string' ? config['DATAPREV_AUTHORIZATION_WEB_LINK'] : '';

  return (
    <>
      <MarginPanel open={open} onOpenChange={onPanelChange} title={titles[phase]} steps={steps} current={phase} onBack={backs[phase]} busy={busy || querying} footer={footers[phase]}>
        {phase === 'form' ? (
          <FormProvider {...form}>
            <form
              id={formId}
              noValidate
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                // O painel mora num portal, mas o evento do React sobe até o formulário de quem abriu.
                event.stopPropagation();
                void form.handleSubmit(submit)();
              }}
            >
              <DocumentFormField<FormValues> name="registrationNumber" label={t`CPF`} required disabled={locked} placeholder={t`000.000.000-00`} />
              {authRequired ? (
                <>
                  <div className="rounded-card bg-muted p-4">
                    <p className="mb-2 font-semibold">{t`Importante`}</p>
                    <p className="text-sm text-muted-foreground">{t`Esse CPF ainda não autorizou a consulta de dados do Crédito do trabalhador. Siga as instruções abaixo para solicitar a autorização.`}</p>
                  </div>
                  <SelectFormField<FormValues>
                    name="authorizationChannel"
                    label={t`Canal de autorização`}
                    required
                    options={[
                      // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
                      { value: 'WhatsappInbound', label: t`Whatsapp Reativo` },
                      { value: 'SMS', label: t`SMS Ativo` },
                      // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
                      { value: 'WebLink', label: t`Link Web` },
                    ]}
                  />
                  {channel === 'WebLink' ? (
                    <section aria-label={t`Link de Autorização Web`} className="flex flex-col gap-3 rounded-card bg-ok-wash p-4 text-ok">
                      <p className="font-semibold">{t`Link de Autorização Web`}</p>
                      <p className="text-sm">{t`Compartilhe o link ou QR Code abaixo com o titular para autorizar a consulta:`}</p>
                      <div className="flex justify-center">
                        <div className="rounded-card bg-white p-2">
                          <QRCodeSVG value={webLink} size={200} level="H" marginSize={4} title={t`QR Code do link de autorização`} />
                        </div>
                      </div>
                      <a href={webLink} target="_blank" rel="noopener noreferrer" className="rounded-card border border-ok bg-card p-2 text-sm break-all text-foreground underline-offset-2 hover:underline">
                        {webLink}
                      </a>
                    </section>
                  ) : (
                    <>
                      <p className="font-semibold">{channel === 'SMS' ? t`Informe o número do celular do titular` : t`Informe o Whatsapp do titular`}</p>
                      <MaskedFormField<FormValues> name="phoneNumber" kind="phone" label={channel === 'SMS' ? t`Celular` : t`Whatsapp`} required />
                    </>
                  )}
                </>
              ) : null}
            </form>
          </FormProvider>
        ) : null}

        {phase === 'authorization' && authorization ? (
          <MarginAuthorizationBody
            authorization={authorization}
            smsText={t`Um SMS foi enviado para o celular do titular solicitando autorização para consulta do Crédito do Trabalhador.`}
            polling={smsPolling}
            waiting={cacheEmpty}
          />
        ) : null}

        {phase === 'select' ? (
          <>
            {margin ? null : <Alert tone="info">{t`Não há dados de margem disponíveis para esse CPF. Clique no botão 'Obter margem' para fazer a primeira consulta`}</Alert>}
            <MarginCards
              label={t`Margens disponíveis`}
              options={options}
              selected={selected}
              single
              // Uma por vez, como o legado marca no Dataprev (SelectWarranty.tsx:63-76 lê a lista do SIAPE).
              onToggle={(key, checked) => setSelected(checked ? [key] : [])}
            />
          </>
        ) : null}

        {phase === 'restrictions' && restrictions ? (
          <div className="flex flex-col gap-3 rounded-card bg-muted p-4">
            {count > 0 ? (
              <>
                <p className="font-semibold">{count === 1 ? t`1 restrição encontrada` : t`${count} restrições encontradas`}</p>
                <ul className="list-disc pl-5">
                  {restrictions.items.map((item) => (
                    <li key={item.messageError} className="font-semibold">
                      {item.messageError}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="font-semibold text-muted-foreground">{restrictions.message}</p>
            )}
          </div>
        ) : null}
      </MarginPanel>
      {confirmElement}
    </>
  );
}

/** "Consultar Crédito Trabalhador" da Operação: o formulário da operação dá o CPF e o produto e recebe a escolha. */
export function DataprevMarginQuery({ note, product, open, onOpenChange }: { readonly note: CreditNote; readonly product: CreditProduct | undefined; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }) {
  const { t } = useLingui();
  const detail = useTabsForm();
  return (
    <DataprevMarginSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t`Consultar crédito trabalhador`}
      registrationNumber={marginDocument(note)}
      personId={note.personId}
      creditProductId={product?.id || formProductId(detail)}
      authorizationProductId={product?.id}
      acceptCnpj
      onApply={(write) => applyMarginWrite(detail, write)}
    />
  );
}
